import type { ElementSize } from "@gettactic/map-builder/src/components/map/helpers/getElementSize";
import {
  type ShapeMetrics,
  ShapeMetricsContext
} from "@gettactic/map-builder/src/components/map/helpers/shapeMetrics";
import React from "react";
import { findShapeNode } from "../gesture/domTransform";
import { exportSvgAsPng } from "../render/exportPng";
import { useEditorStore } from "../store/EditorStoreContext";

export interface ShapeMetricsProviderProps {
  svgRef: React.RefObject<SVGSVGElement | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  children: React.ReactNode;
}

/**
 * Answers the chrome's "where is this shape on screen" questions from the
 * SVG DOM, where the Konva editor reads its stage. Rendered shapes carry
 * `data-shape-id`, so a bounding box is one query away.
 */
export const ShapeMetricsProvider: React.FC<ShapeMetricsProviderProps> = ({
  svgRef,
  containerRef,
  children
}) => {
  const store = useEditorStore();
  const pointerRef = React.useRef<{ x: number; y: number } | null>(null);

  React.useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const track = (event: PointerEvent) => {
      const rect = svg.getBoundingClientRect();
      pointerRef.current = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };
    };
    svg.addEventListener("pointermove", track);
    svg.addEventListener("pointerdown", track);
    return () => {
      svg.removeEventListener("pointermove", track);
      svg.removeEventListener("pointerdown", track);
    };
  }, [svgRef]);

  const metrics = React.useMemo<ShapeMetrics>(
    () => ({
      getScreenRect: (shapeId) => {
        const svg = svgRef.current;
        const node = svg ? findShapeNode(svg, shapeId) : null;
        if (!node) return null;
        const rect = node.getBoundingClientRect();
        return {
          left: rect.left,
          top: rect.top,
          right: rect.right,
          bottom: rect.bottom
        };
      },
      getElementSize: (shapeId): ElementSize | null => {
        const shape = store.getState().shapes[shapeId];
        const svg = svgRef.current;
        const node = svg ? findShapeNode(svg, shapeId) : null;
        if (!shape || !node) return null;
        // The wrapper group has no transform of its own, so its bbox is
        // already in world units.
        const box = node.getBBox();
        return {
          x: shape.x,
          y: shape.y,
          width: shape.width,
          height: shape.height,
          visualX: box.x,
          visualY: box.y,
          visualWidth: box.width,
          visualHeight: box.height
        };
      },
      getPointerPosition: () => pointerRef.current,
      toWorld: (position) => {
        const { x, y, scale } = store.getState().viewport;
        return { x: (position.x - x) / scale, y: (position.y - y) / scale };
      },
      exportPng: async (options) => {
        const svg = svgRef.current;
        return svg ? exportSvgAsPng(svg, options) : false;
      }
    }),
    [store, svgRef]
  );

  void containerRef;

  return (
    <ShapeMetricsContext.Provider value={metrics}>
      {children}
    </ShapeMetricsContext.Provider>
  );
};
